import { DOCUMENT } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, LOCALE_ID } from '@angular/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withNavigationErrorHandler } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { authErrorInterceptor, customLoaderFactory, errorLoggingInterceptor, tokenInterceptor } from '@rms/core/http';
import { provideNavigation } from '@rms/core/navigation';
import { environment } from '@rms/env';
import { provideLayout } from '@rms/layout';

import { appRoutes } from './app.routes';
import { isChunkLoadError, promptReloadForFreshChunks } from './core/error/chunk-load-recovery';
import { GlobalErrorHandler } from './core/error/global-error-handler';
import { provideIcons } from './core/icons/icons.provider';
import { provideLanguageDetection } from './core/language-detection/language-detection.provider';
import { provideLuxon, provideRmsLuxonDateAdapter } from './core/luxon/luxon.provider';
import { NotificationService, provideNotifications } from './core/notification';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: environment.defaultLanguage },
    importProvidersFrom(
      BrowserModule,
      MatDialogModule,
      TranslateModule.forRoot({
        fallbackLang: environment.defaultLanguage,
        loader: { provide: TranslateLoader, useFactory: customLoaderFactory },
      })
    ),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { hasBackdrop: true, disableClose: true, closeOnNavigation: true, autoFocus: false },
    },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    // Must run before provideRouter: langParamResolver waits on the detection this starts.
    provideLanguageDetection(),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
      // Lazy-route chunk 404s after a deploy surface as a NavigationError and never
      // reach the global ErrorHandler, so the reload prompt is wired here as well.
      withNavigationErrorHandler((navigationError) => {
        if (!isChunkLoadError(navigationError.error)) {
          return;
        }

        promptReloadForFreshChunks(inject(NotificationService), inject(DOCUMENT).defaultView);
      })
    ),
    provideAnimations(),
    provideHttpClient(withInterceptors([tokenInterceptor, errorLoggingInterceptor, authErrorInterceptor])),
    provideLayout(),
    provideNavigation(),
    provideIcons(),
    provideNotifications(),
    provideLuxon(),
    provideRmsLuxonDateAdapter(),
    // Registered after importProvidersFrom(BrowserModule) so it overrides BrowserModule's default ErrorHandler.
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
};
